// フォルダの一括削除（削除可否判定→実行）
import { ref, onMounted } from 'vue';
import _ from 'lodash';

export const useFolderBulkDelete = ({
  folders,
  items,
  selectedFolderId,
  basketFolderIds,
  removeFromBasket,
  selectFolder,
  onMessage,
}) => {
  const folderBulkDeleteLoading = ref(false);
  const folderBulkDeleteExecuting = ref(false);
  const folderBulkDeleteDeletable = ref([]);
  const folderBulkDeleteBlocked = ref([]);
  const folderBulkDeleteErrorMessage = ref('');
  const folderBulkDeleteResultMessage = ref('');

  const translate = (key, fallback = '') =>
    _.get(window.i18n, key) || fallback || key;

  // 削除実行中はモーダルを閉じさせない(背後の別フォルダ選択との競合を防ぐため)
  onMounted(() => {
    if (window.jQuery) {
      window.jQuery('#folderBulkDeleteModal').on('hide.bs.modal', (event) => {
        if (folderBulkDeleteExecuting.value) {
          event.preventDefault();
        }
      });
    }
  });

  const resetResultState = () => {
    folderBulkDeleteErrorMessage.value = '';
    folderBulkDeleteResultMessage.value = '';
  };

  const fetchRemovableCheck = async () => {
    const targetIds = basketFolderIds.value;
    if (!targetIds.length) {
      folderBulkDeleteDeletable.value = [];
      folderBulkDeleteBlocked.value = [];
      return;
    }

    folderBulkDeleteLoading.value = true;
    folderBulkDeleteErrorMessage.value = '';
    try {
      const response = await window.axios.post(
        '/dashboard/folders/removable-check',
        { folder_ids: targetIds },
        { headers: { Accept: 'application/json' } }
      );
      folderBulkDeleteDeletable.value = response.data.deletable || [];
      folderBulkDeleteBlocked.value = response.data.blocked || [];
    } catch (error) {
      folderBulkDeleteDeletable.value = [];
      folderBulkDeleteBlocked.value = [];
      folderBulkDeleteErrorMessage.value = translate(
        'folder.bulk_delete_check_failed',
        '削除対象の確認に失敗しました。'
      );
    } finally {
      folderBulkDeleteLoading.value = false;
    }
  };

  const openFolderBulkDeleteModal = () => {
    if (basketFolderIds.value.length < 1) {
      return;
    }
    resetResultState();
    folderBulkDeleteDeletable.value = [];
    folderBulkDeleteBlocked.value = [];
    if (window.jQuery) {
      window.jQuery('#folderBulkDeleteModal').modal('show');
    }
    fetchRemovableCheck();
  };

  const removeFolderFromBulkDelete = (id) => {
    removeFromBasket(id, 'folder');
    resetResultState();
    if (basketFolderIds.value.length >= 1) {
      fetchRemovableCheck();
    } else {
      folderBulkDeleteDeletable.value = [];
      folderBulkDeleteBlocked.value = [];
    }
  };

  const executeFolderBulkDelete = async () => {
    if (folderBulkDeleteDeletable.value.length < 1 || folderBulkDeleteExecuting.value) {
      return;
    }

    folderBulkDeleteExecuting.value = true;
    resetResultState();

    // folders.value から削除対象を除去する前に親フォルダIDを控える
    // (選択中フォルダが削除された場合、削除後にその親フォルダへ選択を戻すため)
    // ルート直下フォルダはサーバが parent_id を 0 でシリアライズすることがあるため、
    // 0/null/undefined はすべて「祖先なし(ルート)」として null に正規化する
    const parentByFolderId = new Map(
      (Array.isArray(folders.value) ? folders.value : []).map((folder) => [
        String(folder.id),
        Number(folder.parent_id) > 0 ? folder.parent_id : null,
      ])
    );

    try {
      const response = await window.axios.post(
        '/dashboard/folders/bulk-remove',
        { folder_ids: basketFolderIds.value },
        { headers: { Accept: 'application/json' } }
      );

      const deletedIds = response.data.deleted_ids || [];
      const rejected = response.data.rejected || [];
      const message = response.data.message || '';

      const deletedIdSet = new Set(deletedIds.map((id) => String(id)));

      const selectedWasDeleted = deletedIdSet.has(String(selectedFolderId.value));
      // 選択中フォルダの親を辿り、今回の削除に含まれない最初の祖先へ選択を移す
      // (親子同時選択の一括削除で、削除済みフォルダが選択状態に残らないようにするため)
      let nextSelectedFolderId = null;
      if (selectedWasDeleted) {
        let cursor = parentByFolderId.get(String(selectedFolderId.value)) ?? null;
        while (cursor !== null && deletedIdSet.has(String(cursor))) {
          cursor = parentByFolderId.get(String(cursor)) ?? null;
        }
        nextSelectedFolderId = cursor;
      }

      if (deletedIds.length > 0) {
        folders.value = (Array.isArray(folders.value) ? folders.value : []).filter(
          (folder) => !deletedIdSet.has(String(folder.id))
        );
        if (items && Array.isArray(items.value)) {
          items.value = items.value.filter(
            (item) => !(item.item_type === 'folder' && deletedIdSet.has(String(item.id)))
          );
        }
        deletedIds.forEach((id) => removeFromBasket(id, 'folder'));
      }

      if (selectedWasDeleted) {
        // 祖先遡りで有効な祖先が見つからない場合(ルート直下フォルダの削除など)は、
        // 削除後も残っているルート直下フォルダの先頭へフォールバック選択する
        if (!(Number(nextSelectedFolderId) > 0)) {
          const fallbackFolder = (
            Array.isArray(folders.value) ? folders.value : []
          ).find((folder) => !(Number(folder.parent_id) > 0));
          nextSelectedFolderId = fallbackFolder ? fallbackFolder.id : null;
        }

        if (Number(nextSelectedFolderId) > 0 && typeof selectFolder === 'function') {
          // 選択とフォルダ内一覧の再取得を標準経路(フォルダクリックと同一処理)で行う
          await selectFolder(nextSelectedFolderId);
        } else {
          selectedFolderId.value = nextSelectedFolderId;
        }
      }

      if (rejected.length === 0) {
        folderBulkDeleteDeletable.value = [];
        folderBulkDeleteBlocked.value = [];
        if (window.jQuery) {
          // hide.bs.modal の実行中ガードが自身の閉じ処理までブロックしないよう、閉じる直前に実行中フラグを解除する
          folderBulkDeleteExecuting.value = false;
          window.jQuery('#folderBulkDeleteModal').modal('hide');
        }
        if (typeof onMessage === 'function') {
          onMessage(message);
        }
      } else {
        // このリクエストで判定された分（削除済み・対象外）はすべて処理済みのため削除対象は空になる
        folderBulkDeleteDeletable.value = [];
        folderBulkDeleteBlocked.value = rejected;
        folderBulkDeleteResultMessage.value = message;
      }
    } catch (error) {
      folderBulkDeleteErrorMessage.value = translate(
        'folder.bulk_delete_execute_failed',
        'フォルダの削除に失敗しました。'
      );
    } finally {
      folderBulkDeleteExecuting.value = false;
    }
  };

  return {
    folderBulkDeleteLoading,
    folderBulkDeleteExecuting,
    folderBulkDeleteDeletable,
    folderBulkDeleteBlocked,
    folderBulkDeleteErrorMessage,
    folderBulkDeleteResultMessage,
    openFolderBulkDeleteModal,
    removeFolderFromBulkDelete,
    executeFolderBulkDelete,
  };
};
